/**
 * 지난 회의(아카이브) 관리 API 클라이언트 — §11 아카이브.
 *
 * `rooms-admin.ts` 와 같은 규율이다: 자격은 Bearer 권한 토큰(§5.2)이고,
 * 인가 판정은 서버의 `_require_room_admin` 이 정본이다. 이 파일은 그
 * 관문에 요청을 보낼 뿐이다.
 *
 * 참가하지 않은 지난 회의의 회의록을 여는 것도 `backupRoom` 과 같은
 * 열람이라 서버가 **감사 로그에 남긴다**(§11.5) — 화면이 그 사실을
 * 숨기지 않는다.
 */

import { API_BASE } from './config'
import { ApiError } from './api'
import { accountAuthHeaders } from './token'
import type { ExportFormat, TranscriptFile } from './export'
import { parseFilename, fallbackFilename } from './export'

/** `server/storage.py` `_STREAM_SCHEMA` 의 `meetings` 테이블 — `files` 는 서버가 뺀다. */
export interface MeetingRow {
  id: number
  room: string
  owner: string
  src: string
  dst: string
  started_at: number
  ended_at: number
  end_reason: string
  retention_days: number
  captions: number
  chats: number
  finalized_at: number
}

const MESSAGES: Record<string, string> = {
  forbidden: '이 회의록을 볼 권한이 없습니다.',
  unauthenticated: '로그인이 필요합니다.',
  not_found: '회의를 찾을 수 없습니다.',
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(await accountAuthHeaders()),
      ...(init?.headers ?? {}),
    },
  })
  const text = await res.text()
  let body: unknown = {}
  try {
    body = text ? JSON.parse(text) : {}
  } catch {
    body = {}
  }
  if (!res.ok) {
    const detail = (body as { detail?: unknown }).detail
    const code = typeof detail === 'string' ? detail : 'unknown'
    throw new ApiError(res.status, code, MESSAGES[code] ?? '요청이 실패했습니다.')
  }
  return body as T
}

/** 지난(종료된) 회의 전체 — 관리자. */
export function listMeetings(): Promise<{ meetings: MeetingRow[] }> {
  return req('/admin/meetings')
}

/**
 * 지난 회의의 회의록을 받아 온다(`GET /api/admin/meetings/{code}/export`).
 *
 * `backupRoom` 과 달리 **받자마자 저장하지 않는다** — 화면에 먼저 보여
 * 주는 뷰어 용도라서다. 내려받기는 이미 받은 blob 을 그대로 쓴다.
 */
export async function fetchMeetingTranscript(
  code: string,
  fmt: ExportFormat,
): Promise<TranscriptFile> {
  const res = await fetch(
    `${API_BASE}/admin/meetings/${encodeURIComponent(code)}/export?fmt=${fmt}`,
    { credentials: 'same-origin', headers: await accountAuthHeaders() },
  )
  if (!res.ok) {
    let code_ = 'unknown'
    try {
      const body = (await res.json()) as { detail?: unknown }
      if (typeof body.detail === 'string') code_ = body.detail
    } catch {
      /* 본문이 없거나 JSON 이 아니면 상태만 남긴다 */
    }
    throw new ApiError(res.status, code_, MESSAGES[code_] ?? '회의록을 받지 못했습니다.')
  }
  const blob = await res.blob()
  return {
    blob,
    filename:
      parseFilename(res.headers.get('Content-Disposition')) || fallbackFilename(code, fmt),
    partial: res.headers.get('X-Transcript-Partial') === '1',
    count: Number(res.headers.get('X-Transcript-Count') ?? 0),
  }
}
